import React, { useState, useEffect, useMemo } from 'react';
import '../Reports/Challenge.css';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const WalkinEnquiry = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedCenter } = useUser();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { userData } = useUser();
  const token = localStorage.getItem('auth-token');

  const [academyOptions, setAcademyOptions] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(null);

  const [sportOptions, setSportOptions] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);

  const [enquiryForOptions, setEnquiryForOptions] = useState([]);
  const [selectedEnquiryFor, setSelectedEnquiryFor] = useState(null);

  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetch(process.env.REACT_APP_API_URL_FEEDBACK_FETCH, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data?.success) return;

        let options = Object.entries(data.data.academyNameMap).map(([id, name]) => ({
          value: Number(id),
          label: name,
        }));

        if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
          const allowed = new Set(userData.academyIds.map(Number));
          options = options.filter((opt) => allowed.has(Number(opt.value)));
        }

        setAcademyOptions(options);

        if (selectedCenter) {
          const def = options.find((opt) => Number(opt.value) === Number(selectedCenter));
          if (def) setSelectedAcademy(def.value);
        }
      })
      .catch((err) => {
        console.error('Error fetching academy options:', err);
      });
  }, [selectedCenter, token, userData?.userType]);

  const validateDateRange = () => {
    const s = new Date(dateRange.start);
    const e = new Date(dateRange.end);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return true; 
    if (s > e) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid date range',
        text: 'Start date cannot be after end date.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return false;
    }
    return true;
  };

  const fetchWalkinEnquiry = async () => {
    if (!validateDateRange()) return;

    setLoading(true);
    setError('');

    try {
      let academyQuery = '';
      if (userData?.userType === 'ACADEMY') {
        const allowedAcademies = selectedAcademy ? [selectedAcademy] : userData.academyIds;
        if (!allowedAcademies || allowedAcademies.length === 0) {
          setError('No academy access defined for this user.');
          setLoading(false);
          return;
        }
        academyQuery = `&academyIds=${allowedAcademies.join(',')}`;
      } else if (selectedAcademy) {
        academyQuery = `&academyIds=${selectedAcademy}`;
      }

      const apiEndpoint = `${process.env.REACT_APP_API_URL_WALK_FETCH}?fromDate=${dateRange.start}&toDate=${dateRange.end}${academyQuery}`;
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await axios.get(apiEndpoint, { headers });

      if (response.data.success) {
        const { academyNameMap, sportNameMap, enquiries } = response.data.data;

        const sports = Object.entries(sportNameMap).map(([id, name]) => ({
          value: Number(id),
          label: name,
        }));
        setSportOptions(sports);

        const enquiryForOpts = [...new Set(enquiries.map((enq) => enq.enquiryFor))].map((value) => ({
          value,
          label: value,
        }));
        setEnquiryForOptions(enquiryForOpts);

        const mapped = (enquiries || []).map((item) => {
          const createdAt = item.createdAt ? new Date(item.createdAt) : null;
          return {
            id: item.id,
            centreId: item.academyId ?? null,
            centre: academyNameMap[item.academyId] || '-',
            name: item.name || '-',
            phone: item.mobileNumber || '-',
            date: createdAt ? createdAt.toLocaleDateString('en-GB') : '-',
            timestamp: createdAt ? createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-',
            _createdAtRaw: createdAt ? createdAt.getTime() : 0, 
            sportId: item.sportId ?? null,
            sport: sportNameMap[item.sportId] || '-',
            enquiryFor: item.enquiryFor || '-',
            email: item.email || '-',
            comments: item.enquiry || '-',
            remarks: (item.remarks || '').trim(),
            isSolved: Number(item.isSolved ?? 0),
          };
        });

        setEnquiries(mapped);
      } else {
        setError('Failed to load data: No enquiries available.');
      }
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      setError('Error fetching data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalkinEnquiry();
  }, [dateRange, selectedAcademy, userData?.userType]);

  const headers = [
    { label: 'Centre', key: 'centre', isNumeric: false },
    { label: 'Name', key: 'name', isNumeric: false },
    { label: 'Phone', key: 'phone', isNumeric: false },
    { label: 'Email', key: 'email', isNumeric: false },
    { label: 'Date', key: 'date', isNumeric: false },
    { label: 'Sport', key: 'sport', isNumeric: false },
    { label: 'Enquiry For', key: 'enquiryFor', isNumeric: false },
    { label: 'Enquiry', key: 'comments', isNumeric: false },
    { label: 'Remarks', key: 'remarks', isNumeric: false },
    { label: 'Timestamp', key: 'timestamp', isNumeric: false },
  ];

  const handleOpenModal = async (enquiry) => {
    const isCurrentlySolved = !!(enquiry.remarks && enquiry.remarks.trim());

    const { value: formValues } = await Swal.fire({
      title: isCurrentlySolved ? 'Update Enquiry Status' : 'Add Enquiry Status',
      html: `
        <div style="font-family: 'Segoe UI', sans-serif; font-size: 14px; color: #333;">
          <div>
            <label style="font-weight: 600; display: block; text-align: left; margin-bottom: 10px;">Status</label>
            <div style="display: flex; gap: 30px;">
              <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                <input type="radio" name="status" value="Solved" id="solved" ${isCurrentlySolved ? 'checked' : ''} />
                <span>Solved</span>
              </label>
              <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                <input type="radio" name="status" value="Unsolved" id="unsolved" ${!isCurrentlySolved ? 'checked' : ''} />
                <span>Unsolved</span>
              </label>
            </div>
          </div>
  
          <div style="margin-bottom: 10px;">
            <label for="remark" style="font-weight: 600; text-align: left; display: block; margin-bottom: 8px;">Remark</label>
            <textarea 
              id="remark" 
              placeholder="Enter your remark here"
              style="
                width: 100%;
                height: 100px;
                padding: 10px;
                font-size: 14px;
                border: 1px solid #ccc;
                border-radius: 6px;
                resize: vertical;
                box-sizing: border-box;
                font-family: inherit;
              "
            >${enquiry.remarks || ''}</textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      focusConfirm: false,
      customClass: { popup: 'swal2-custom-popup' },
      preConfirm: () => {
        const status = document.querySelector('input[name="status"]:checked')?.value;
        const remark = document.getElementById('remark')?.value ?? '';
        if (!status || !remark.trim()) {
          Swal.showValidationMessage('Please select a status and enter a remark.');
          return;
        }
        return { status, remark: remark.trim() };
      },
    });

    if (!formValues) return;

    const isSolvedFlag = formValues.status === 'Solved' ? 1 : 0;

    const updatedEnquiry = {
      id: enquiry.id,
      academyId: enquiry.centreId,
      name: enquiry.name,
      mobileNumber: enquiry.phone,
      sportId: enquiry.sportId,
      enquiryFor: enquiry.enquiryFor,
      remarks: formValues.remark,
      isSolved: isSolvedFlag,
      email: enquiry.email,
    };

    if (!updatedEnquiry.academyId || !updatedEnquiry.mobileNumber || !updatedEnquiry.sportId) {
      Swal.fire('Error', 'Some required fields are missing.', 'error');
      return;
    }

    try {
      await axios.post(process.env.REACT_APP_API_URL_WALK_POST, updatedEnquiry, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Enquiry Updated!',
        text: 'The enquiry has been updated successfully.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      setEnquiries((prev) =>
        prev.map((item) =>
          item.id === enquiry.id
            ? { ...item, remarks: formValues.remark, isSolved: isSolvedFlag }
            : item
        )
      );
    } catch (err) {
      console.error('Error updating enquiry:', err);
      Swal.fire('Error', 'Something went wrong while updating the enquiry.', 'error');
    }
  };

  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc',
  });

  const handleItemsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredEnquiries = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    return enquiries.filter((e) => {
      const matchesSearch = Object.values(e).join(' ').toLowerCase().includes(term);
      const matchesAcademy = selectedAcademy ? e.centreId === selectedAcademy : true;
      const matchesSport = selectedSport ? e.sportId === selectedSport : true;
      const matchesEnquiryFor = selectedEnquiryFor ? e.enquiryFor === selectedEnquiryFor : true;
      return matchesSearch && matchesAcademy && matchesSport && matchesEnquiryFor;
    });
  }, [enquiries, searchTerm, selectedAcademy, selectedSport, selectedEnquiryFor]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      let direction = 'asc';
      if (prev.key === key && prev.direction === 'asc') direction = 'desc';
      return { key, direction };
    });
  };

  const sortedEnquiries = useMemo(() => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    return [...filteredEnquiries].sort((a, b) => {
      if (key === 'date' || key === 'timestamp') {
        const va = a._createdAtRaw || 0;
        const vb = b._createdAtRaw || 0;
        return va === vb ? 0 : va < vb ? -dir : dir;
      }

      if (key === 'phone') {
        const na = parseInt(String(a.phone).replace(/\D/g, ''), 10) || 0;
        const nb = parseInt(String(b.phone).replace(/\D/g, ''), 10) || 0;
        return na === nb ? 0 : na < nb ? -dir : dir;
      }

      const va = String(a[key] ?? '').toLowerCase();
      const vb = String(b[key] ?? '').toLowerCase();
      if (va === vb) return 0;
      return va < vb ? -dir : dir;
    });
  }, [filteredEnquiries, sortConfig]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedEnquiries.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedEnquiries.length / rowsPerPage);

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );

  const totalEntries = filteredEnquiries.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">
      <div className="sales-filters-leadManagement">
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedAcademy ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={academyOptions}
            value={academyOptions.find((o) => o.value === selectedAcademy) || null}
            onChange={(sel) => setSelectedAcademy(sel ? sel.value : null)}
            placeholder="Academy"
            isClearable
            styles={{ placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {selectedAcademy && <label htmlFor="academy">Academy</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedSport ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={sportOptions}
            value={sportOptions.find((o) => o.value === selectedSport) || null}
            onChange={(sel) => setSelectedSport(sel ? sel.value : null)}
            placeholder="Sport"
            isClearable
            styles={{ placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {selectedSport && <label htmlFor="sport">Sport</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${selectedEnquiryFor ? 'has-value' : ''}`}
            classNamePrefix="react-select"
            options={enquiryForOptions}
            value={enquiryForOptions.find((o) => o.value === selectedEnquiryFor) || null}
            onChange={(sel) => setSelectedEnquiryFor(sel ? sel.value : null)}
            placeholder="Enquiry For"
            isClearable
            styles={{ placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {selectedEnquiryFor && <label htmlFor="enquiryFor">Enquiry For</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${dateRange.start ? 'has-value' : ''}`}
            value={dateRange.start}
            onChange={(e) => setDateRange((d) => ({ ...d, start: e.target.value }))}
            onFocus={(e) => e.target.showPicker()}
          />
          {dateRange.start && <label htmlFor="startDate">Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${dateRange.end ? 'has-value' : ''}`}
            value={dateRange.end}
            onChange={(e) => setDateRange((d) => ({ ...d, end: e.target.value }))}
            onFocus={(e) => e.target.showPicker()}
          />
          {dateRange.end && <label htmlFor="endDate">End Date</label>}
        </div>
      </div>

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>

        <CSVLink
          data={filteredEnquiries.map(({ _createdAtRaw, isSolved, ...rest }) => rest)}
          headers={headers}
          filename="WalkinEnquiry.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button">
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="tt-action-btn" onClick={fetchWalkinEnquiry}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredEnquiries.length === 0 && <div className="error-message">No data available</div>}

      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr className="challenges-header-row">
              {headers.map(({ label, key, isNumeric }) => (
                <th key={key} className={`challenges-column ${isNumeric ? 'numeric' : ''}`}>
                  <div
                    onClick={() => handleSort(key)}
                    className="th-content sortable-column"
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${
                        sortConfig.key === key && sortConfig.direction === 'desc' ? 'desc' : ''
                      }`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'CENTRE_MANAGEMENT', 'WALK_IN_ENQUIRY_MODIFY') && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((enquiry) => (
              <tr key={enquiry.id} className="challenges-row">
                <td className="challenges-cell">{enquiry.centre}</td>
                <td className="challenges-cell">{enquiry.name}</td>
                <td className="challenges-cell">{enquiry.phone}</td>
                <td className="challenges-cell">{enquiry.email}</td>
                <td className="challenges-cell">{enquiry.date}</td>
                <td className="challenges-cell">{enquiry.sport}</td>
                <td className="challenges-cell">{enquiry.enquiryFor}</td>
                <td className="challenges-cell">{enquiry.comments}</td>
                <td className="challenges-cell">{enquiry.remarks}</td>
                <td className="challenges-cell">{enquiry.timestamp}</td>
                {hasPrivilege(userData, 'CENTRE_MANAGEMENT', 'WALK_IN_ENQUIRY_MODIFY') && (
                  <td>
                    <button className="resolve-button" onClick={() => handleOpenModal(enquiry)}>
                      {enquiry.remarks ? 'Update Remark' : 'Add Remark'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>
          Previous
        </button>
        {renderPagination()}
        <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
};

export default WalkinEnquiry;
